import ProductCell from "@/core/components/ProductCell";
import { formatRupiah } from "@/core/helpers/string";
import { useProductAutocomplete } from "@/features/product/usecases/useProductAutocomplete";
import {
	type AutocompleteFetchParams,
	Badge,
	Button,
	Can,
	DataTable,
	type DataTableFiltersProp,
	toast,
	useDialogController,
} from "@k3-tech/react-kit";
import type { ColumnDef } from "@tanstack/react-table";
import { EditIcon, LayersIcon, PlusIcon, TrashIcon } from "lucide-react";
import { useMemo, useState } from "react";
import {
	resolveMenuImagePrefill,
	resolveMenuImagePreview,
	resolveMenuImageType,
} from "../usecases/resolveMenuImageType";
import type { SalesChannelCategoryGroupProductRow } from "../usecases/useSalesChannelCategoryGroupProductList";
import { useSalesChannelCategoryGroupProductMutations } from "../usecases/useSalesChannelCategoryGroupProductMutations";
import { useSalesChannelCategoryGroupProductPagedList } from "../usecases/useSalesChannelCategoryGroupProductPagedList";
import { GroupProductAddonDialog } from "./GroupProductAddonDialog";
import {
	type GroupProductFormValues,
	SalesChannelGroupProductFormDialog,
} from "./SalesChannelGroupProductFormDialog";

export interface SalesChannelGroupProductTableProps {
	groupID: string;
	categoryOptions: { value: string; label: string }[];
	categoryNameByID: Map<string, string>;
	onChanged?: () => void;
}

const productLabel = (row: SalesChannelCategoryGroupProductRow) =>
	row.product?.productName || row.productName || "-";
const productCode = (row: SalesChannelCategoryGroupProductRow) =>
	row.product?.productCode || row.productCode || row.barcode || "";

export function SalesChannelGroupProductTable({
	groupID,
	categoryOptions,
	categoryNameByID,
	onChanged,
}: SalesChannelGroupProductTableProps) {
	const { confirm } = useDialogController();
	const products = useSalesChannelCategoryGroupProductPagedList(groupID);
	const mutations = useSalesChannelCategoryGroupProductMutations();
	const { load: loadProduct } = useProductAutocomplete();

	const productFetcher = (params: AutocompleteFetchParams) =>
		loadProduct(params.page, params.pageSize, params.search, params.moreFilter);

	const [dialog, setDialog] = useState<
		| { mode: "create" }
		| { mode: "edit"; row: SalesChannelCategoryGroupProductRow }
		| null
	>(null);
	const [addonRow, setAddonRow] =
		useState<SalesChannelCategoryGroupProductRow | null>(null);

	const submit = async (values: GroupProductFormValues) => {
		const payload = {
			salesChannelCategoryGroupID: groupID,
			salesChannelCategoryID: values.salesChannelCategoryID,
			productID: values.productID,
			isActive: values.isActive,
			// Custom price is hidden from the UI for now — always pricelist.
			priceType: "pricelist",
			customPrice: 0,
			externalImageURL: values.menuImage?.[0]?.url || "",
			imageType: resolveMenuImageType(values.menuImage?.[0]?.url),
		};
		const result =
			dialog?.mode === "edit"
				? await mutations.update({
						...payload,
						// Product is locked on edit, so its field is empty — take it from the row.
						productID: dialog.row.productID,
						salesChannelCategoryGroupProductID:
							dialog.row.salesChannelCategoryGroupProductID,
					})
				: await mutations.create(payload);
		if (result.error) {
			toast.error(result.error.message);
			throw result.error;
		}
		toast.success(
			dialog?.mode === "edit" ? "Product updated" : "Product added",
		);
		setDialog(null);
		products.load();
		onChanged?.();
	};

	const remove = async (row: SalesChannelCategoryGroupProductRow) => {
		const ok = await confirm({
			title: `Remove ${productLabel(row)}?`,
			description: "This action cannot be undone.",
			destructive: true,
			confirmText: "Remove",
			cancelText: "Cancel",
		});
		if (!ok) return;
		const result = await mutations.remove(
			row.salesChannelCategoryGroupProductID,
		);
		if (result.error) {
			toast.error(result.error.message);
			return;
		}
		toast.success("Product removed");
		products.load();
		onChanged?.();
	};

	const columns: ColumnDef<SalesChannelCategoryGroupProductRow>[] = [
		{
			header: () => <div className="min-w-[300px]">Product</div>,
			id: "product",
			cell: ({ row }) => {
				const previewURL = resolveMenuImagePreview(row.original);
				return (
					<div className="flex items-center gap-2">
						{previewURL ? (
							<img
								src={previewURL}
								alt=""
								className="h-8 w-8 shrink-0 rounded object-cover bg-slate-100"
							/>
						) : (
							<div className="h-8 w-8 shrink-0 rounded bg-slate-100" />
						)}
						{/* ProductCell is min-w-full, so it needs its own shrinkable box next to the thumbnail */}
						<div className="min-w-0 flex-1">
							<ProductCell
								productCode={productCode(row.original)}
								productName={productLabel(row.original)}
								barcode={row.original.product?.barcode ?? row.original.barcode}
							/>
						</div>
					</div>
				);
			},
		},
		{
			header: "Category",
			id: "category",
			cell: ({ row }) =>
				categoryNameByID.get(row.original.salesChannelCategoryID) ?? "-",
		},
		{
			header: "Add-ons",
			id: "addons",
			cell: ({ row }) =>
				row.original.product?.hasAddon ? (
					<Button
						variant="ghost"
						size="sm"
						className="h-7 gap-1 px-2 text-teal-600"
						title="View add-ons"
						onClick={() => setAddonRow(row.original)}
					>
						<LayersIcon className="h-3.5 w-3.5" />
						<span className="text-xs">
							{row.original.product?.addonGroup?.name || "Add-ons"}
						</span>
					</Button>
				) : (
					<span className="text-slate-400">—</span>
				),
		},
		{
			header: "Price",
			id: "price",
			cell: ({ row }) =>
				row.original.priceType === "custom" ? (
					<Badge variant="secondary" className="text-teal-600">
						{`Custom · ${formatRupiah(row.original.customPrice)}`}
					</Badge>
				) : (
					<Badge variant="secondary">Pricelist</Badge>
				),
		},
		{
			header: "Status",
			id: "status",
			cell: ({ row }) => (
				<Badge
					variant="secondary"
					className={row.original.isActive ? "text-green-600" : undefined}
				>
					{row.original.isActive ? "Active" : "Inactive"}
				</Badge>
			),
		},
		{
			header: "",
			id: "actions",
			cell: ({ row }) => (
				<div className="flex items-center justify-end gap-1">
					<Can permissions="sales_channel_category_groups.edit">
						<Button
							variant="ghost"
							size="sm"
							className="h-7 w-7 p-0"
							title="Edit"
							onClick={() => setDialog({ mode: "edit", row: row.original })}
						>
							<EditIcon className="h-3.5 w-3.5" />
						</Button>
					</Can>
					<Can permissions="sales_channel_category_groups.delete">
						<Button
							variant="ghost"
							size="sm"
							className="h-7 w-7 p-0"
							title="Remove"
							onClick={() => remove(row.original)}
						>
							<TrashIcon className="h-3.5 w-3.5" />
						</Button>
					</Can>
				</div>
			),
		},
	];

	const formFilters = useMemo<DataTableFiltersProp>(
		() => [
			{
				variant: "plain",
				layout: "flex",
				flex: { direction: "row", wrap: true, gap: "gap-3" },
				fields: [
					{
						name: "productName",
						label: " ",
						type: "text",
						placeholder: "Search product",
					},
					{
						name: "salesChannelCategoryID",
						label: " ",
						type: "select",
						className: "w-full",
						placeholder: "Category",
						options: [{ label: "All", value: null }, ...categoryOptions],
					},
				],
			},
		],
		[categoryOptions],
	);

	const onFormFilterChange = (values: Record<string, unknown>) => {
		products.setFilters({
			productName: values.productName || null,
			salesChannelCategoryID: values.salesChannelCategoryID || null,
		});
	};

	return (
		<>
			<div className="mb-3 flex justify-end">
				<Can permissions="sales_channel_category_groups.create">
					<Button
						size="sm"
						variant="outline"
						onClick={() => setDialog({ mode: "create" })}
					>
						<PlusIcon className="h-3.5 w-3.5" />
						Add
					</Button>
				</Can>
			</div>
			<DataTable<SalesChannelCategoryGroupProductRow, unknown>
				columns={columns}
				data={products.items as SalesChannelCategoryGroupProductRow[]}
				filterWrapper="none"
				filterShowActionsSeparator={false}
				loading={products.fetching}
				formFilters={formFilters}
				onFormFilterChange={onFormFilterChange}
				pagination={products.rowCount > 0}
				paginationState={products.paginationState}
				onPaginationChange={products.setPagination}
				rowCount={products.rowCount}
				showStandardActions
				onRefresh={products.load}
			/>

			<SalesChannelGroupProductFormDialog
				open={!!dialog}
				onOpenChange={(open) => !open && setDialog(null)}
				mode={dialog?.mode ?? "create"}
				categoryOptions={categoryOptions}
				productAutocompleteFetcher={productFetcher}
				initialProduct={
					dialog?.mode === "edit"
						? { value: dialog.row.productID, label: productLabel(dialog.row) }
						: undefined
				}
				defaultValues={
					dialog?.mode === "edit"
						? {
								productID: dialog.row.productID,
								salesChannelCategoryID: dialog.row.salesChannelCategoryID,
								priceType: dialog.row.priceType,
								customPrice: dialog.row.customPrice,
								menuImage: resolveMenuImagePrefill(dialog.row),
								isActive: dialog.row.isActive,
							}
						: undefined
				}
				onSubmit={submit}
			/>

			<GroupProductAddonDialog
				open={!!addonRow}
				onOpenChange={(open) => !open && setAddonRow(null)}
				productName={addonRow ? productLabel(addonRow) : ""}
				addonGroupID={addonRow?.product?.addonGroupID}
				addonGroupName={addonRow?.product?.addonGroup?.name}
			/>
		</>
	);
}
